import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { UserPlus, Copy, Link2, Trash2, XCircle, CheckCircle2, Clock, Mail, MessageSquare, Power } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const inviteLink = (token) => `${window.location.origin}/join/${token}`;

const STATUS = {
  pending: { label: "Pending", cls: "bg-[#FFC43D]/20 text-[#946200] border-[#FFC43D]/50", icon: Clock },
  accepted: { label: "Joined", cls: "bg-[#2EC4B6]/15 text-[#0d7d72] border-[#2EC4B6]/40", icon: CheckCircle2 },
  revoked: { label: "Revoked", cls: "bg-[#FF6B6B]/15 text-[#c0392b] border-[#FF6B6B]/40", icon: XCircle },
};

export default function CrewManager() {
  const [invites, setInvites] = useState([]);
  const [crew, setCrew] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", channels: ["email"], type: "single" });
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const load = useCallback(async () => {
    try {
      const [i, c] = await Promise.all([api.get("/invites"), api.get("/crew")]);
      setInvites(i.data);
      setCrew(c.data);
    } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (ch) => setForm((f) => ({
    ...f,
    channels: f.channels.includes(ch) ? f.channels.filter((x) => x !== ch) : [...f.channels, ch],
  }));

  const copy = async (token) => {
    try { await navigator.clipboard.writeText(inviteLink(token)); toast.success("Invite link copied"); }
    catch { toast.error("Could not copy — long-press the link to copy"); }
  };

  const sendInvite = async () => {
    if (!form.name.trim()) return toast.error("Add the person's name");
    if (!form.email.trim() && !form.phone.trim()) return toast.error("Add an email and/or phone");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        channels: form.channels.length ? form.channels : ["email"],
        single_use: form.type === "single",
        expires_hours: form.type === "expiring" ? 48 : null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/invites", payload);
      setLastLink({ name: data.name, token: data.token });
      setForm({ name: "", email: "", phone: "", channels: ["email"], type: "single" });
      toast.success("Invite created — share the link below");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try { await api.post(`/invites/${id}/revoke`); toast.success("Invite revoked"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleCrew = async (m) => {
    try { await api.patch(`/crew/${m.id}`, { active: !m.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeCrew = async (m) => {
    if (!window.confirm(`Remove ${m.name} from your crew? Their assigned tasks will be unassigned.`)) return;
    try { await api.delete(`/crew/${m.id}`); toast.success("Crew member removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="crew-manager" className="space-y-6">
      {/* Invite form */}
      <div className="border border-[var(--line)] bg-white p-5">
        <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-4"><UserPlus className="w-4 h-4" /> Invite a crew member</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input data-testid="invite-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          <input data-testid="invite-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          <input data-testid="invite-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (+1…)" className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-4">
          <span className="label-mono text-[var(--text-2)]">Send via</span>
          <button data-testid="invite-ch-email" onClick={() => toggleChannel("email")} className={`flex items-center gap-1.5 h-9 px-3 border text-sm transition-colors ${form.channels.includes("email") ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/5" : "border-[var(--line)] text-[var(--text-2)]"}`}><Mail className="w-4 h-4" /> Email</button>
          <button data-testid="invite-ch-sms" onClick={() => toggleChannel("sms")} className={`flex items-center gap-1.5 h-9 px-3 border text-sm transition-colors ${form.channels.includes("sms") ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/5" : "border-[var(--line)] text-[var(--text-2)]"}`}><MessageSquare className="w-4 h-4" /> SMS</button>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-4">
          <span className="label-mono text-[var(--text-2)]">Link type</span>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input data-testid="invite-type-single" type="radio" name="invite-type" checked={form.type === "single"} onChange={() => set("type", "single")} className="accent-[var(--accent)]" /> Single-use
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input data-testid="invite-type-expiring" type="radio" name="invite-type" checked={form.type === "expiring"} onChange={() => set("type", "expiring")} className="accent-[var(--accent)]" /> Expires in 48 hours
          </label>
        </div>

        <button data-testid="invite-send" onClick={sendInvite} disabled={busy} className="mt-5 h-11 px-5 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors disabled:opacity-50">
          {busy ? "Creating…" : "Create invite"}
        </button>

        {lastLink && (
          <div data-testid="invite-result" className="mt-5 border border-[var(--accent)]/40 bg-[var(--accent)]/5 p-4 flex flex-col sm:flex-row items-start gap-4">
            <div className="bg-white p-2 border border-[var(--line)]">
              <QRCodeCanvas value={inviteLink(lastLink.token)} size={104} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Invite for {lastLink.name}</p>
              <p className="text-xs text-[var(--text-2)] mt-1 break-all font-mono">{inviteLink(lastLink.token)}</p>
              <button data-testid="invite-copy-last" onClick={() => copy(lastLink.token)} className="mt-3 inline-flex items-center gap-2 h-9 px-3 border border-[var(--line)] bg-white text-sm hover:border-[var(--accent)] transition-colors">
                <Copy className="w-4 h-4" /> Copy link
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invites list */}
      <div className="border border-[var(--line)] bg-white p-5">
        <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-4"><Link2 className="w-4 h-4" /> Invitations</p>
        {invites.length === 0 ? (
          <p className="text-sm text-[var(--text-2)]">No invitations yet.</p>
        ) : (
          <div className="space-y-2">
            {invites.map((inv) => {
              const s = STATUS[inv.status] || STATUS.pending; const I = s.icon;
              return (
                <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="flex flex-wrap items-center gap-3 border border-[var(--line)] bg-[#f9f9f7] p-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border flex items-center gap-1 ${s.cls}`}><I className="w-3 h-3" /> {s.label}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{inv.name}</p>
                    <p className="text-xs text-[var(--text-2)] truncate font-mono">{inv.email || inv.phone} · {inv.single_use ? "single-use" : "48h expiry"}</p>
                  </div>
                  {inv.status === "pending" && (
                    <div className="flex items-center gap-2">
                      <button data-testid={`invite-copy-${inv.id}`} onClick={() => copy(inv.token)} className="inline-flex items-center gap-1.5 h-8 px-3 border border-[var(--line)] bg-white text-xs hover:border-[var(--accent)] transition-colors"><Copy className="w-3.5 h-3.5" /> Copy link</button>
                      <button data-testid={`invite-revoke-${inv.id}`} onClick={() => revoke(inv.id)} className="inline-flex items-center gap-1.5 h-8 px-3 border border-[var(--line)] bg-white text-xs hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors"><XCircle className="w-3.5 h-3.5" /> Revoke</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Crew roster */}
      <div className="border border-[var(--line)] bg-white p-5">
        <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-4"><UserPlus className="w-4 h-4" /> Crew members</p>
        {crew.length === 0 ? (
          <p className="text-sm text-[var(--text-2)]">No crew members have joined yet.</p>
        ) : (
          <div className="space-y-2">
            {crew.map((m) => (
              <div key={m.id} data-testid={`crew-row-${m.id}`} className="flex flex-wrap items-center gap-3 border border-[var(--line)] bg-[#f9f9f7] p-3">
                <div className={`w-2 h-2 rounded-full ${m.active ? "bg-[#2EC4B6]" : "bg-[#FF6B6B]"}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{m.name} {!m.active && <span className="text-xs text-[var(--danger)]">(deactivated)</span>}</p>
                  <p className="text-xs text-[var(--text-2)] truncate font-mono">
                    {m.email || m.phone} · {m.task_count} task{m.task_count === 1 ? "" : "s"}
                    {m.last_checkin ? ` · last check-in ${new Date(m.last_checkin.created_at).toLocaleString()}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`crew-toggle-${m.id}`} onClick={() => toggleCrew(m)} className="inline-flex items-center gap-1.5 h-8 px-3 border border-[var(--line)] bg-white text-xs hover:border-[var(--accent)] transition-colors"><Power className="w-3.5 h-3.5" /> {m.active ? "Deactivate" : "Reactivate"}</button>
                  <button data-testid={`crew-remove-${m.id}`} onClick={() => removeCrew(m)} className="inline-flex items-center gap-1.5 h-8 px-3 border border-[var(--line)] bg-white text-xs hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
